import { inject, Injectable, Injector } from '@angular/core';
import { BehaviorSubject, Observable, Subscription, combineLatest, firstValueFrom, of, timer } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, shareReplay, switchMap, tap, withLatestFrom } from 'rxjs/operators';

import {
    entitySyncComplete,
    entitiesSyncCompleteBatch,
    startEntitySync,
    startEntitiesSyncBatch,
    SelectSyncStatus,
    entitySyncError,
    removeEntityFromChangeCache,
    EntityChangeSelector
} from '../data';
import { Force, ForceService } from '../../forces';
import { snapshot } from '../utils';
import { selectForces } from '../../forces/state/selectors';
import { FORCE_ACTIONS } from '../../forces/state/actions';
import { LoginSelector } from '../settings/service/selectors';

import { BaseSyncService, SYNC_DEBOUNCE } from './sync.base';

const mapToForceId = (f) => f.forceId;
const getForceIdsByState = (forces, state) => forces.filter((f) => f.state === state).map(mapToForceId);

const getCacheHash = (cache) => {
    if (!cache) {
        return '';
    }
    return cache.map(({ forceId, state }) => `${forceId}:${state}`).join('|');
};

/**
 * Service for synchronizing force data between client and server.
 *
 * Supports both individual and batch operations for improved performance:
 * - Batch downloads for multiple forces in a single request
 * - Graceful fallback to individual downloads if batch operations fail
 * - Handles partial batch failures by retrying failed forces individually
 *
 * The batch download endpoint expects: POST /userData/forces/batch { forceIds: string[] }
 * And returns: { data: Force[], errors?: { forceId: string, error: string }[] }
 */
@Injectable({ providedIn: 'root' })
export class ForceSyncService extends BaseSyncService {
    entityType = 'forces';
    syncStatusSubscription: Subscription;

    // Use inject() to lazily get ForceService to avoid circular dependency
    private injector = inject(Injector);
    private _forceService: ForceService;
    private get forceService(): ForceService {
        if (!this._forceService) {
            this._forceService = this.injector.get(ForceService);
        }
        return this._forceService;
    }

    forceCache$: Observable<Force[]> = this.store.select(selectForces).pipe(shareReplay(1));
    loggedIn$ = this.store.select(LoginSelector).pipe(map((loginState) => !!loginState.user && loginState.checked));
    entityChangeCache$ = this.store.select(EntityChangeSelector).pipe(
        map((s) => s.forces),
        map((forces) => Object.entries(forces || {}).map((f) => ({ forceId: f[0], state: f[1] }))),
        distinctUntilChanged((prev, current) => getCacheHash(prev) === getCacheHash(current)),
        // tap((x) => console.log('!!! forceSyncService.entityChangeCache$ emitted a new value')),
        debounceTime(100),
        shareReplay(1)
    );

    entityChangeCacheByState$ = this.entityChangeCache$.pipe(
        map((forces) => {
            let byState = {};
            for (const force of forces) {
                if (!byState[force.state]) {
                    byState[force.state] = [];
                }
                byState[force.state].push(force.forceId);
            }
            return byState;
        })
    );

    deletedForces$ = this.entityChangeCacheByState$.pipe(map((x) => x['Deleted'] || []));
    dirtyForces$ = this.entityChangeCacheByState$.pipe(map((x) => x['Dirty'] || []));
    cleanForces$ = this.entityChangeCacheByState$.pipe(map((x) => x['Clean'] || []));
    erroredForces$ = this.entityChangeCacheByState$.pipe(map((x) => x['Error'] || []));

    init() {
        setTimeout(() => {
            this.deletedForces$.pipe(debounceTime(SYNC_DEBOUNCE)).subscribe((toDelete) => {
                this.uploadForces({ toSync: [], toDelete });
            });
            this.dirtyForces$
                .pipe(
                    switchMap((forceIds) => {
                        const switchMapStartTime = performance.now();
                        return this.forceCache$.pipe(
                            map((forces) => {
                                const filtered = forces.filter((f) => forceIds.includes(f.id));
                                return filtered;
                            })
                        );
                    }),
                    debounceTime(SYNC_DEBOUNCE)
                )
                .subscribe((toSync) => {
                    this.uploadForces({ toSync, toDelete: [] });
                });
            if (!this.syncStatusSubscription) {
                let firstSyncInProgress = false;
                this.syncStatusSubscription = this.syncStatus$.subscribe((val) => {
                    // console.log(val);
                    if (val.status === 'SYNCING' && !this.firstSyncComplete && !firstSyncInProgress) {
                        firstSyncInProgress = true;
                    } else if (val.status === 'IDLE') {
                        firstSyncInProgress = false;
                        setTimeout(() => {
                            this.firstSyncComplete = true;
                        }, this.MIN_LOADING_DELAY);
                    }
                });
            }
        }, 1000);
    }

    /**
     * Main sync method - checks server for updates and downloads newer versions of CLEAN forces.
     * Dirty forces are uploaded separately via the dirtyForces$ subscription.
     *
     * Last-Writer-Wins: We only download updates for forces that don't have local changes.
     * Forces with local changes (dirty) will overwrite server when they upload.
     *
     * @param options.manual - If true, always show sync status (user-initiated).
     *                         If false/undefined (periodic), only show status if there are changes.
     */
    async sync(options?: { manual?: boolean }) {
        const manual = options?.manual ?? false;

        // Check if user is logged in before attempting sync
        const loggedIn = await firstValueFrom(this.loggedIn$);
        if (!loggedIn) {
            return;
        }

        // If manual sync, immediately show syncing status for visual feedback
        if (manual) {
            this.syncStatus$.next({ status: 'SYNCING' });
        }

        const serverTimestamps = await firstValueFrom(this.getServerTimestamps());
        const forceIdsOnServer = Object.keys(serverTimestamps).map((x) => '' + x);
        const localForces = await firstValueFrom(this.getForcesFromState());
        const deletedForces = await firstValueFrom(this.deletedForces$);
        const dirtyForceIds = await firstValueFrom(this.dirtyForces$);

        const forceIdsToDownload = [];

        for (const id of forceIdsOnServer) {
            // Skip forces that are deleted locally
            if (deletedForces.includes(id)) {
                continue;
            }

            const serverTs = serverTimestamps[id];
            const localForce = localForces.find((f) => f.id === id);

            // Skip forces that have local changes - they will be uploaded and overwrite server
            // BUT: if force doesn't exist locally, download it even if marked dirty
            // (this handles case where page is refreshed before local changes sync)
            if (dirtyForceIds.includes(id)) {
                if (!localForce) {
                    // Force missing locally but marked dirty - restore from server
                    forceIdsToDownload.push(id);
                }
                // If force exists locally and is dirty, skip download (keep local version)
                continue;
            }

            // Download if: force doesn't exist locally, OR server is newer
            if (!localForce || localForce.updatedAt < serverTs) {
                forceIdsToDownload.push(id);
            }
        }

        const hasChanges = forceIdsToDownload.length > 0 || dirtyForceIds.length > 0;

        // For periodic syncs, only show status if there are actual changes
        if (!manual && hasChanges) {
            this.syncStatus$.next({ status: 'SYNCING' });
        }

        if (forceIdsToDownload.length === 0) {
            setTimeout(() => this.refreshSyncStatus(), this.MIN_LOADING_DELAY);
        } else {
            this.syncStatus$.next({ status: 'SYNCING', message: `Downloading ${forceIdsToDownload.length} forces` });
            this.downloadForcesBatch(forceIdsToDownload);
        }
    }

    /**
     * Upload dirty forces (Last-Writer-Wins - no timestamp comparison needed)
     * Forces in toSync are assumed to be dirty and will be uploaded
     */
    uploadForces(forces: { toSync: Force[]; toDelete: string[] }, serverTimestamps: { [key: string]: number } = null) {
        // Upload all dirty forces - use batch for multiple, individual for single
        if (forces.toSync.length > 1) {
            this.uploadForcesBatch(forces.toSync);
        } else if (forces.toSync.length === 1) {
            this.syncForce(forces.toSync[0], serverTimestamps?.[forces.toSync[0].id]);
        }

        // Handle deletions
        forces.toDelete.forEach((forceId) => {
            const url = `${this.config.apiBaseUrl}/userData/forces/${forceId}`;
            const entityChangeCachePayload = { entityType: 'forces', entityId: forceId };
            this.httpClient
                .delete(url, null, {
                    headers: { ...this.config.globalRequestHeaders },
                    withCredentials: true,
                    requiresLogin: true
                })
                .pipe(
                    catchError((_err) => {
                        this.store.dispatch(entitySyncError(entityChangeCachePayload));
                        return null;
                    }),
                    filter((x) => !!x)
                )
                .subscribe((res) => {
                    this.store.dispatch(removeEntityFromChangeCache(entityChangeCachePayload));
                });
        });
    }

    private getForcesFromState() {
        return this.store.select(selectForces).pipe(
            map((forces) => {
                return [
                    ...forces.map((f: Force) => {
                        let units = f.units;
                        if (!units && (f as any).data?.units) {
                            // Fixes a data bug caused by the old sync process
                            units = (f as any).data.units;
                        }

                        if (!units && (f as any).changes?.units) {
                            // Fixes a data bug caused by the old sync process
                            units = (f as any).changes.units;
                        }

                        return {
                            ...f,
                            units: units.map((u) => ({
                                ...u,
                                unitTemplate: undefined
                            }))
                        };
                    })
                ].filter((f) => f);
            })
        );
    }

    /**
     * Last-Writer-Wins sync logic:
     * - If force is Dirty → Upload (local changes take priority)
     * - If force is Clean AND server is newer → Download
     * - No conflict detection - last writer wins
     */
    syncForce(_force: Force, serverTimestamp: number = null) {
        const force = {
            ..._force,
            appVersion: this.config.version,
            units: _force.units.map((unit) => {
                // Stripe unit template to reduce payload size
                const u = structuredClone(unit);
                delete u.unitTemplate;
                return u;
            })
        };

        // Set global status to SYNCING for visual feedback (status bar)
        this.syncStatus$.next({ status: 'SYNCING' });

        // Also set the individual force status to InProgress
        this.start('forces', force.id);

        snapshot(this.getForceStatus(force), async (forceStatus) => {
            // Note: forceStatus will now be 'InProgress' since we just set it above
            // We check if it was already in progress before we set it by checking syncStartTimes

            const lastSyncTime = force.updatedAt || 0;
            const existsOnServer = lastSyncTime > 0;

            // If force doesn't exist on server yet, upload it
            if (!existsOnServer) {
                this.uploadForce(force);
                return;
            }

            // Check the actual dirty state from the store (before we set InProgress)
            const dirtyForceIds = await firstValueFrom(this.dirtyForces$);
            const isDirty = dirtyForceIds.includes(force.id);

            // LAST-WRITER-WINS: If dirty, always upload (local wins)
            if (isDirty) {
                this.uploadForce(force);
                return;
            }

            // Force is clean - check if server has updates to download
            if (serverTimestamp === null) {
                serverTimestamp = await firstValueFrom(this.getServerTimestamp(force.id));
            }

            if (lastSyncTime < serverTimestamp) {
                this.downloadForce(force.id);
            } else {
                // Complete the sync since there's nothing to do
                this.complete('forces', force.id);
            }
        });
    }

    /**
     * Check a specific force for server updates (used when navigating to a force)
     * Only downloads if the force is clean and server has newer data
     */
    async checkForceForUpdates(forceId: string): Promise<void> {
        const loggedIn = await firstValueFrom(this.loggedIn$);
        if (!loggedIn) {
            return;
        }

        const forces = await firstValueFrom(this.forceCache$);
        const force = forces.find((f) => f.id === forceId);
        if (!force) {
            return;
        }

        const forceStatus = await firstValueFrom(this.getForceStatus(force));

        // Don't check if force has local changes - those will be uploaded
        if (forceStatus === 'Dirty' || forceStatus === 'InProgress') {
            return;
        }

        try {
            const serverTimestamp = await firstValueFrom(this.getServerTimestamp(forceId));
            const localTimestamp = force.updatedAt || 0;

            if (localTimestamp < serverTimestamp) {
                this.start('forces', forceId);
                this.downloadForce(forceId);
            }
        } catch (err) {
            // Silently fail - this is a background check
        }
    }

    addForceToState(force: Force) {
        snapshot(combineLatest([this.dirtyForces$, this.deletedForces$]), async ([_dirtyForces, deletedForces]) => {
            if (deletedForces.includes(force.id)) {
                return;
            }

            // Invalidate the ForceService's postProcessCache for this force
            // so that the updated force data is reprocessed instead of using stale cached data
            this.forceService.invalidateCache(force.id);

            const action = FORCE_ACTIONS.UPSERT_FORCE_WITHOUT_UPLOAD({ force });
            this.store.dispatch(action);

            // Mark as complete with minimum display delay
            this.complete('forces', force.id);

            // Immediately persist to storage to prevent data loss if user refreshes
            // before the debounced persistence kicks in
            await this.forceService.persistImmediately();
        });
    }

    handleSyncError(forceId: string) {
        return catchError((err) => {
            this.error('forces', forceId);
            throw err;
        });
    }

    downloadForce(forceId: string) {
        const headers = this.config.globalRequestHeaders;
        const url = this.getSyncUrl(forceId);
        this.httpClient
            .get(url, { headers, withCredentials: true, requiresLogin: true })
            .pipe(this.handleSyncError(forceId))
            .subscribe((serverData: { data: any }) => {
                this.addForceToState(serverData.data);
            });
    }

    downloadForcesBatch(forceIds: string[]) {
        const headers = this.config.globalRequestHeaders;
        const batchUrl = `${this.config.apiBaseUrl}/userData/forces/batch`;

        this.httpClient
            .post(
                batchUrl,
                { forceIds },
                {
                    headers,
                    withCredentials: true,
                    requiresLogin: true
                }
            )
            .pipe(
                catchError((err) => {
                    // Fallback to individual downloads if batch fails
                    forceIds.forEach((id, i) => {
                        this.syncStatus$.next({
                            status: 'SYNCING',
                            message: `Downloading force ${i + 1}/${forceIds.length} (batch fallback)`
                        });
                        this.downloadForce(id);
                    });
                    return null;
                }),
                filter((x) => !!x)
            )
            .subscribe((batchResponse: { data: { data: Force }[]; errors?: { id: string; error: string }[] }) => {
                if (batchResponse?.data && Array.isArray(batchResponse.data)) {
                    // Process all successfully downloaded forces
                    batchResponse.data.forEach((force) => {
                        this.addForceToState(force.data);
                    });

                    // Handle partial failures - retry individual downloads for failed forces
                    if (batchResponse.errors && batchResponse.errors.length > 0) {
                        batchResponse.errors.forEach(({ id }) => {
                            this.downloadForce(id);
                        });
                    }
                } else {
                    // Fallback to individual downloads
                    forceIds.forEach((id) => {
                        this.downloadForce(id);
                    });
                }

                // Refresh sync status after batch is complete
                setTimeout(() => this.refreshSyncStatus(), 1000);
            });
    }

    uploadForcesBatch(forces: Force[]) {
        const headers = this.config.globalRequestHeaders;
        const batchUrl = `${this.config.apiBaseUrl}/userData/forces/batch`;

        // Mark all forces as in progress using batch action
        const entityIds = forces.map((f) => f.id);
        this.store.dispatch(startEntitiesSyncBatch({ entityType: 'forces', entityIds }));

        this.httpClient
            .put(
                batchUrl,
                { forces },
                {
                    headers,
                    withCredentials: true,
                    requiresLogin: true
                }
            )
            .pipe(
                catchError((err) => {
                    // Fallback to individual uploads if batch fails
                    forces.forEach((force) => {
                        this.uploadForce(force);
                    });
                    return of(null);
                }),
                filter((x) => !!x)
            )
            .subscribe((batchResponse: { data: { id: string; updatedAt: number }[]; errors?: { id: string; error: string }[] }) => {
                if (batchResponse?.data && Array.isArray(batchResponse.data)) {
                    // Batch update all timestamps in a single dispatch
                    const timestampUpdates = batchResponse.data.map(({ id, updatedAt }) => ({
                        id,
                        updatedAt
                    }));

                    if (timestampUpdates.length > 0) {
                        this.store.dispatch(
                            FORCE_ACTIONS.UPDATE_FORCES_BATCH_TIMESTAMPS({
                                forces: timestampUpdates
                            })
                        );

                        // Mark all as complete using batch action
                        const completedIds = timestampUpdates.map(({ id }) => id);
                        this.store.dispatch(entitiesSyncCompleteBatch({ entityType: 'forces', entityIds: completedIds }));
                    }

                    // Handle partial failures - retry individual uploads for failed forces
                    if (batchResponse.errors && batchResponse.errors.length > 0) {
                        batchResponse.errors.forEach(({ id }) => {
                            const force = forces.find((f) => f.id === id);
                            if (force) {
                                this.uploadForce(force);
                            }
                        });
                    }
                } else {
                    // Fallback to individual uploads
                    forces.forEach((force) => {
                        this.uploadForce(force);
                    });
                }

                // Refresh sync status after batch is complete
                setTimeout(() => this.refreshSyncStatus(), 1000);
            });
    }

    uploadForce(force: any) {
        const headers = this.config.globalRequestHeaders;
        let promise: Observable<number>;

        if (force.updatedAt) {
            promise = this.httpClient.put(this.getSyncUrl(force.id), force, {
                headers,
                withCredentials: true,
                requiresLogin: true
            });
        } else {
            promise = this.httpClient.post(this.getSyncUrl(), force, {
                headers,
                withCredentials: true,
                requiresLogin: true
            });
        }

        promise.pipe(this.handleSyncError(force.id)).subscribe((newTimestamp: number) => {
            // Only update the timestamp, don't overwrite the entire force.
            // This preserves any changes the user may have made during the upload.
            this.store.dispatch(
                FORCE_ACTIONS.UPDATE_FORCE_TIMESTAMP({
                    id: force.id,
                    updatedAt: newTimestamp
                })
            );
            this.complete('forces', force.id);
        });
    }

    protected getForceStatus(force: any) {
        return this.store.select(SelectSyncStatus, { entityType: 'forces', id: force.id });
    }

    protected getSettingsStatus() {
        return this.store.select(SelectSyncStatus, { entityType: 'settings' });
    }
}
